import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Sun, Moon, LayoutGrid } from 'lucide-react';
import { GiGardeningShears, GiFeather } from 'react-icons/gi';
import { MdCleaningServices } from 'react-icons/md';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { NAV_LINKS, SITE_CONFIG } from '@/lib/constants';
import { useThemeStore } from '@/stores/themeStore';

const serviceItems = [
  { id: 'gardening', title: 'Gardening', href: '/services/gardening', icon: GiGardeningShears, description: 'Lawn & garden care' },
  { id: 'cleaning', title: 'Cleaning', href: '/services/cleaning', icon: MdCleaningServices, description: 'Home & business' },
  { id: 'taxidermy', title: 'Taxidermy', href: '/services/taxidermy', icon: GiFeather, description: 'Preservation art' },
];

/**
 * Header component with responsive navigation, logo, and theme toggle
 */
export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }
  }, [theme]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container-custom" aria-label="Main navigation">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label={`${SITE_CONFIG.name} - Home`}
          >
            <img
              src="/logo/logo-trans.png"
              alt="Cloud9 Logo"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-1">
            {NAV_LINKS.map((link) =>
              link.label === 'Services' ? (
                <div key={link.href} className="relative">
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    onBlur={() => setTimeout(() => setServicesOpen(false), 200)}
                    className={cn(
                      'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive(link.href)
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                    aria-expanded={servicesOpen}
                    aria-haspopup="true"
                  >
                    {link.label}
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        servicesOpen && 'rotate-180'
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  {servicesOpen && (
                    <div
                      className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-border bg-popover p-2 shadow-xl"
                      role="menu"
                    >
                      {/* All Services Link */}
                      <Link
                        to="/services"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg hover:bg-muted transition-colors"
                        role="menuitem"
                        onClick={() => setServicesOpen(false)}
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <LayoutGrid className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">All Services</p>
                          <p className="text-xs text-muted-foreground">View overview</p>
                        </div>
                      </Link>

                      <div className="my-2 h-px bg-border" />

                      {/* Individual Services */}
                      {serviceItems.map((service) => (
                        <Link
                          key={service.id}
                          to={service.href}
                          className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg hover:bg-muted transition-colors"
                          role="menuitem"
                          onClick={() => setServicesOpen(false)}
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                            <service.icon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{service.title}</p>
                            <p className="text-xs text-muted-foreground">{service.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive(link.href)
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Right side: Theme toggle + CTA */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Moon className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>

            {/* Separator - Desktop only */}
            <div className="hidden md:block h-6 w-px bg-border" />

            {/* Contact CTA - Desktop */}
            <div className="hidden md:block">
              <Button asChild>
                <Link to="/contact">Get Quote</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open navigation menu"
                  aria-expanded={isOpen}
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <img
                      src="/logo/logo-trans.png"
                      alt="Cloud9 Logo"
                      className="h-12 w-auto"
                    />
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col gap-4" aria-label="Mobile navigation">
                  {NAV_LINKS.map((link) =>
                    link.label === 'Services' ? (
                      <div key={link.href} className="flex flex-col gap-3">
                        <Link
                          to="/services"
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            'text-lg font-medium transition-colors',
                            isActive('/services')
                              ? 'text-primary'
                              : 'text-foreground hover:text-primary'
                          )}
                        >
                          Services
                        </Link>
                        <div className="ml-2 flex flex-col gap-2">
                          {serviceItems.map((service) => (
                            <Link
                              key={service.id}
                              to={service.href}
                              onClick={() => setIsOpen(false)}
                              className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted transition-colors"
                            >
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-foreground">
                                <service.icon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{service.title}</p>
                                <p className="text-xs text-muted-foreground">{service.description}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          'text-lg font-medium transition-colors',
                          isActive(link.href)
                            ? 'text-primary'
                            : 'text-foreground hover:text-primary'
                        )}
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                  <Button asChild className="mt-4">
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      Get Quote
                    </Link>
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
